<template>
  <Card
    :footerDivider="false"
    :loadingState="showAddress === false && showError === false"
    :title="$t('addressConfirmation.title')"
  >
    <template v-slot:content>
      <div v-if="showAddress">
        <p>
          {{ $t('addressConfirmation.content') }}
        </p>
        <Radiogroup
          @input="onRadioChanged"
          class="mt-32"
          name="address-confirmation"
          :value="selectedAddressOption"
          :styleOption="typedRadioStyleOption"
          :items="[{ id: 'confirm', label: $t('addressConfirmation.sentTo') }]"
        ></Radiogroup>
        <div
          :class="{ 'address-secondary-color': isSecondaryStyle }"
          class="with_margin_bottom_baseline"
        >
          <!-- eslint-disable-next-line @typescript-eslint/no-explicit-any -->
          <div v-if="name" class="card-row address-blue-color">
            {{ (name as any)?.firstName }} {{ (name as any)?.name }}
          </div>
          <!-- eslint-disable-next-line @typescript-eslint/no-explicit-any -->
          <div class="card-row address-blue-color">
            {{ (partnerAddress?.address as any)?.street }}
          </div>
          <!-- eslint-disable-next-line @typescript-eslint/no-explicit-any -->
          <div class="card-row address-blue-color">
            {{ (partnerAddress?.address as any)?.streetExtra }}
          </div>
          <!-- eslint-disable-next-line @typescript-eslint/no-explicit-any -->
          <div class="card-row address-blue-color">
            {{ (partnerAddress?.address as any)?.postCode }}
            {{ (partnerAddress?.address as any)?.city }}
          </div>
          <!-- eslint-disable-next-line @typescript-eslint/no-explicit-any -->
          <div class="card-row address-blue-color">
            {{ (partnerAddress?.address as any)?.country?.value }}
          </div>
        </div>
        <Radiogroup
          @input="onRadioChanged"
          class="mt-16"
          name="address-confirmation"
          :value="selectedAddressOption"
          :styleOption="typedRadioStyleOption"
          :items="[{ id: 'change', label: $t('addressConfirmation.changeAddress') }]"
        ></Radiogroup>
        <Alert
          v-if="addressChangeChecked && !loggedIn"
          class="alert mt-40"
          type="warning"
          :size="AlertSize.MEDIUM"
        >
          <template v-slot:main>
            <div>
              <strong>{{ $t('addressConfirmation.addressChanged') }}</strong>
              <span v-dompurify-html="$t('addressConfirmation.customerService')"></span>
            </div>
          </template>
        </Alert>
      </div>
      <template v-if="showError">
        <div class="card-row with_margin_bottom_baseline">
          <p class="error">
            {{ $t('addressConfirmation.error') }} <br />
            <span v-dompurify-html="$t('addressConfirmation.customerService')"></span>
          </p>
        </div>
      </template>
    </template>
    <template v-if="showAddress" v-slot:footer>
      <div class="footer-cta footer-cta--left">
        <CustomButton
          v-if="!addressChangeChecked"
          @click="sendUnlockCode"
          :buttonColor="ButtonColor.PRIMARY"
          :disabledState="confirmAddressButtonDisabled()"
          :pendingState="loading"
          buttonId="confirm-address-button"
          >{{ $t('addressConfirmation.buttonConfirmAddress') }}
        </CustomButton>
        <CustomButton
          v-if="addressChangeChecked && !loggedIn"
          @click="gotoUnlockCodeSms"
          :buttonColor="ButtonColor.PRIMARY"
          buttonId="button-goto-sms"
          >{{ $t('addressConfirmation.buttonGotoSms') }}
        </CustomButton>
        <CustomButton
          v-if="addressChangeChecked && !loggedIn"
          @click="gotoActivationSuccess"
          :buttonColor="ButtonColor.LINK"
          buttonId="button-goto-activation-success"
          >{{ $t('addressConfirmation.buttonContinue') }}
        </CustomButton>
        <CustomButton
          v-if="addressChangeChecked && loggedIn"
          @click="gotoChangeAddress"
          :buttonColor="ButtonColor.PRIMARY"
          buttonId="button-goto-change-address-logged-in"
          >{{ $t('addressConfirmation.buttonChangeAddress') }}
        </CustomButton>
      </div>
    </template>
  </Card>
</template>

<script lang="ts" setup>
import {
  Alert,
  AlertSize,
  ButtonColor,
  Card,
  CustomButton,
  Radiogroup,
  StyleOption
} from 'de-zurich-ui-library'
import { navigate } from '@/utils/NavigationUtils'
import dayjs, { Dayjs } from 'dayjs'
import { getAccountId, getPartnerProxyId, isAuthenticated } from '@/utils/KeycloakUtils'
import { computed, onMounted, type PropType, ref, type Ref } from 'vue'
import {
  type AccountInformationLoggedOut,
  type NaturalPersonName,
  RequestUnlockCodeState
} from '@/types/account-management/AccountManagementTypes'
import {
  fetchAccountsByPPID,
  getPartnerAddressForLoggedOutUser,
  getPartnerByPartnerProxyId,
  sendUnlockCodeLoggedIn,
  sendUnlockCodeLoggedOut
} from '@/stores/accountManagementService'
import type { PartnerAddress } from 'account-api'
import type { PartnerAddress as PartnerApiPartnerAddress } from 'partner-api'

const props = defineProps({
  accountInformationLoggedOut: {
    type: Object as PropType<AccountInformationLoggedOut>,
    required: false
  },
  radioStyleOption: {
    type: String as PropType<StyleOption>,
    default: StyleOption.PRIMARY
  }
})

const emit = defineEmits<{
  onAddressConfirmationStateChanged: [
    state: RequestUnlockCodeState,
    unlockCodeDate?: string
  ]
}>()

const showAddress = ref(false)
const showError = ref(false)
const partnerAddress: Ref<PartnerAddress | PartnerApiPartnerAddress | undefined> =
  ref(undefined)
const partnerAddressId: Ref<number | undefined> = ref(undefined)
const name: Ref<NaturalPersonName | undefined> = ref()
const selectedAddressOption = ref('')
const confirmAddressChecked = computed(() => selectedAddressOption.value === 'confirm')
const addressChangeChecked = computed(() => selectedAddressOption.value === 'change')
const typedRadioStyleOption = computed(() => props.radioStyleOption as StyleOption)
const isSecondaryStyle = computed(() => props.radioStyleOption === StyleOption.SECONDARY)
const loading = ref(false)
const loggedIn = ref(false)

const onRadioChanged = (value: string): void => {
  selectedAddressOption.value = value
}

onMounted(async () => {
  loggedIn.value = isAuthenticated()

  if (!loggedIn.value && !props.accountInformationLoggedOut) {
    throw new Error('No accountInformationLoggedOut available but authenticated false.')
  }

  if (!loggedIn.value && props.accountInformationLoggedOut) {
    await getPartnerAddressLoggedOut(props.accountInformationLoggedOut)
  }

  if (loggedIn.value) {
    await getPartnerAddressLoggedIn()
  }
})

const confirmAddressButtonDisabled = (): boolean => {
  return !confirmAddressChecked.value || showError.value
}

const gotoChangeAddress = (): void => {
  navigate(globalThis.ENV_CONFIG?.REDIRECTS.PERSONAL_DATA_CHANGE_ADDRESS_PAGE)
}

const gotoUnlockCodeSms = (): void => {
  emit('onAddressConfirmationStateChanged', RequestUnlockCodeState.CHOICE)
}

const gotoActivationSuccess = (): void => {
  emit(
    'onAddressConfirmationStateChanged',
    RequestUnlockCodeState.SEND_UNLOCKCODE_LETTER_SUCCESS_ADDRESS_CHANGE
  )
}

const getDateFormatISO8601 = (date: string): string => {
  const momentDate: Dayjs = dayjs(date, 'DD.MM.YYYY', true)
  if (momentDate.isValid()) {
    return momentDate.format('YYYY-MM-DD')
  }
  return ''
}

const sendUnlockCode = (): void => {
  if (loggedIn.value) {
    sendUnlockCodeLoggedInHandler()
  } else {
    sendUnlockCodeLoggedOutHandler()
  }
}

const sendUnlockCodeLoggedInHandler = async (): Promise<void> => {
  const accountId = getAccountId()
  if (!accountId || !partnerAddressId.value) {
    emit('onAddressConfirmationStateChanged', RequestUnlockCodeState.ERROR_LETTER)
    return
  }

  loading.value = true
  try {
    await sendUnlockCodeLoggedIn(accountId, partnerAddressId.value)
    emit(
      'onAddressConfirmationStateChanged',
      RequestUnlockCodeState.SEND_UNLOCKCODE_LETTER_SUCCESS
    )
  } catch {
    emit('onAddressConfirmationStateChanged', RequestUnlockCodeState.ERROR_LETTER)
  } finally {
    loading.value = false
  }
}

const sendUnlockCodeLoggedOutHandler = async (): Promise<void> => {
  if (!props.accountInformationLoggedOut?.accountId) {
    console.error('No accountId available!')
    showError.value = true
    showAddress.value = false
    return
  }
  const accountId = props.accountInformationLoggedOut.accountId
  const proxyId = partnerAddress.value?.partnerProxyIds?.[0]

  if (!proxyId) {
    showError.value = true
    showAddress.value = false
    return
  }

  loading.value = true
  try {
    await sendUnlockCodeLoggedOut(accountId, proxyId)
    emit(
      'onAddressConfirmationStateChanged',
      RequestUnlockCodeState.SEND_UNLOCKCODE_LETTER_SUCCESS
    )
  } catch {
    showAddress.value = false
    showError.value = true
  } finally {
    loading.value = false
  }
}

const getPartnerAddressLoggedOut = async (
  accountInformationLoggedOut: AccountInformationLoggedOut
): Promise<void> => {
  const formattedBirthdate = getDateFormatISO8601(accountInformationLoggedOut.birthdate)
  try {
    const result = await getPartnerAddressForLoggedOutUser(
      accountInformationLoggedOut.accountId,
      formattedBirthdate
    )
    partnerAddress.value = result
    if (result?.partner?.person) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const person = result.partner.person as any
      name.value = person.name
    }
    if (!result?.partnerProxyIds || result.partnerProxyIds.length === 0) {
      navigate(globalThis.ENV_CONFIG?.REDIRECTS.TECHNICAL_ERROR_PAGE)
      return
    }
    showAddress.value = true
  } catch {
    navigate(globalThis.ENV_CONFIG?.REDIRECTS.TECHNICAL_ERROR_PAGE)
  }
}

const getPartnerAddressLoggedIn = async () => {
  const partnerProxyId = getPartnerProxyId()
  if (!partnerProxyId) {
    emit('onAddressConfirmationStateChanged', RequestUnlockCodeState.ERROR_LETTER)
    loading.value = false
    console.error('No partner proxy id available!')
    return
  }

  loading.value = true
  try {
    const accounts = await fetchAccountsByPPID(partnerProxyId)
    if (accounts[0]?.pukLetterCreated) {
      emit(
        'onAddressConfirmationStateChanged',
        RequestUnlockCodeState.UNLOCK_CODE_EXISTS,
        dayjs(accounts[0].pukLetterCreated).format('DD.MM.YYYY')
      )
    } else {
      await getPartnerAddressByPartner(partnerProxyId)
      showAddress.value = true
    }
  } catch {
    navigate(globalThis.ENV_CONFIG?.REDIRECTS.TECHNICAL_ERROR_PAGE)
  } finally {
    loading.value = false
  }
}

const getPartnerAddressByPartner = async (partnerProxyId: string) => {
  try {
    const partner = await getPartnerByPartnerProxyId(partnerProxyId)
    if (partner?.partnerAddresses && partner.partnerAddresses.length > 0) {
      partnerAddress.value = partner.partnerAddresses[0]
      if (partner.partnerAddresses[0]?.partnerAddressId === undefined) {
        emit(
          'onAddressConfirmationStateChanged',
          RequestUnlockCodeState.MISSING_PARTNER_ADDRESS_ID
        )
      } else {
        partnerAddressId.value = partner.partnerAddresses[0].partnerAddressId
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const personData = partner.person as any
        name.value = personData?.name
      }
    } else {
      emit('onAddressConfirmationStateChanged', RequestUnlockCodeState.ERROR_LETTER)
    }
  } catch {
    emit('onAddressConfirmationStateChanged', RequestUnlockCodeState.ERROR_LETTER)
  } finally {
    loading.value = false
  }
}
</script>

<style lang="scss" scoped>
.address-blue-color {
  margin-left: 36px;
  font-weight: bold;
  color: var(--info-color);
}

.address-secondary-color .address-blue-color {
  color: var(--secondary-color-2);
}

:deep() .checkbox__label {
  font-size: var(--medium-font-size) !important;
}

.mt-40 {
  margin-top: 40px;
}

.alert {
  font-family: var(--f-primary-base), serif;
}
</style>
